import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Dashboard.css';

interface DashboardProps {
  setIsAuthenticated: (value: boolean) => void;
}

interface TariffPart {
  id: number;
  PartNo: string;
  PartDescription: string;
  MID: string;
  CountryOfOrigin: string;
  TariffNo: string;
  TariffDescription: string;
  SpecificRate: number;
  AdValoremRate: number;
  Active: string;
  created_at: string;
  updated_at: string;
}

interface UploadStats {
  inserted: number;
  updated: number;
  errors: number;
  total: number;
}

const Dashboard: React.FC<DashboardProps> = ({ setIsAuthenticated }) => {
  const [tariffParts, setTariffParts] = useState<TariffPart[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadStats, setUploadStats] = useState<UploadStats | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTariffParts();
  }, []);

  const fetchTariffParts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/tariff-parts', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setTariffParts(response.data);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
        console.error('Error fetching data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
      setUploadMessage('');
      setUploadStats(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setUploadMessage('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    setUploadMessage('Uploading...');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUploadMessage(response.data.message);
      setUploadStats(response.data.stats);
      setFile(null);

      // Reset file input
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      // Refresh data
      fetchTariffParts();
    } catch (error: any) {
      setUploadMessage(error.response?.data?.error || 'Upload failed');
      setUploadStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/tariff-parts/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchTariffParts();
    } catch (error) {
      console.error('Error deleting record:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    setIsAuthenticated(false);
  };

  const filteredParts = tariffParts.filter((part) => {
    const search = searchTerm.toLowerCase();
    return (
      part.PartNo.toLowerCase().includes(search) ||
      (part.PartDescription && part.PartDescription.toLowerCase().includes(search)) ||
      (part.MID && part.MID.toLowerCase().includes(search)) ||
      part.TariffNo.toLowerCase().includes(search) ||
      (part.TariffDescription && part.TariffDescription.toLowerCase().includes(search)) ||
      (part.CountryOfOrigin && part.CountryOfOrigin.toLowerCase().includes(search))
    );
  });

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Vendor Portal - Tariff Parts Management</h1>
        <div className="header-actions">
          <span>Welcome, {localStorage.getItem('username')}</span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="upload-section">
        <h2>Import Excel File</h2>
        <div className="upload-controls">
          <input
            type="file"
            id="file-input"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={loading}
          />
          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="upload-button"
          >
            Import Data
          </button>
        </div>
        {uploadMessage && (
          <div className={`upload-message ${uploadStats ? 'success' : 'error'}`}>
            {uploadMessage}
          </div>
        )}
        {uploadStats && (
          <div className="upload-stats">
            <div className="stat">
              <span className="stat-label">Inserted:</span>
              <span className="stat-value">{uploadStats.inserted}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Updated:</span>
              <span className="stat-value">{uploadStats.updated}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Errors:</span>
              <span className="stat-value error">{uploadStats.errors}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total:</span>
              <span className="stat-value">{uploadStats.total}</span>
            </div>
          </div>
        )}
      </div>

      <div className="data-section">
        <div className="data-header">
          <h2>Tariff Parts Data</h2>
          <input
            type="text"
            placeholder="Search parts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Part No</th>
                  <th>Part Description</th>
                  <th>MID</th>
                  <th>Country</th>
                  <th>Tariff No</th>
                  <th>Tariff Description</th>
                  <th>Specific Rate</th>
                  <th>Ad Valorem Rate</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredParts.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="no-data">
                      No data available. Please import an Excel file.
                    </td>
                  </tr>
                ) : (
                  filteredParts.map((part) => (
                    <tr key={part.id}>
                      <td>{part.PartNo}</td>
                      <td>{part.PartDescription || '-'}</td>
                      <td>{part.MID || '-'}</td>
                      <td>{part.CountryOfOrigin || '-'}</td>
                      <td>{part.TariffNo}</td>
                      <td>{part.TariffDescription || '-'}</td>
                      <td>{part.SpecificRate || 0}</td>
                      <td>{parseFloat(part.AdValoremRate?.toString() || '0').toFixed(4)}</td>
                      <td>{part.Active || 'Y'}</td>
                      <td>
                        <button
                          onClick={() => handleDelete(part.id)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;